import React, { useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { SearchInput } from '../../components/SearchInput';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import Paginator from '../../components/Paginator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useTrans } from '../../hooks/useTrans';

export default function UsersIndex({ clients, filters }) {
    const { auth } = usePage().props;
    const canCreateUsers = auth?.permissions?.usersCreate;
    const translate = useTrans();
    const [query, setQuery] = useState(filters.query || '');

    function handleSubmit(e) {
        e.preventDefault();
        router.get(window.location.pathname, { query }, { preserveState: true });
    }

    function handleClearSearch() {
        setQuery('');
        router.get(window.location.pathname, {}, { preserveState: true });
    }

    function handleDelete(clientId, clientName) {
        if (!clientId) return;
        if (window.confirm(translate('Weet je zeker dat je :name wilt verwijderen?', { name: clientName }))) {
            router.delete(`/gebruikers/${clientId}`);
        }
    }

    const hasClients = clients && clients.data && clients.data.length > 0;

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            <div className="bg-white rounded-[3px] border border-[#bbb]">
                <div className="p-[30px] pb-0">
                    <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2 mb-6">
                        <h3 className="text-2xl font-bold text-[#003545]">
                            {translate('Gebruikers')}
                        </h3>
                        <div className="flex items-center gap-2 ml-auto">
                            <SearchInput
                                name="query"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onClear={handleClearSearch}
                                showClear={!!filters.query}
                                placeholder={translate('Zoek op naam of e-mailadres')}
                                className="min-w-[352px]"
                            />
                            <Button variant="primary" type="submit">
                                {translate('Zoeken')}
                            </Button>
                            {canCreateUsers && (
                                <Button variant="primary" asChild>
                                    <Link href="/gebruikers/aanmaken">{translate('Nieuwe gebruiker uitnodigen')}</Link>
                                </Button>
                            )}
                        </div>
                    </form>
                </div>

                <div className="px-[30px] pb-[30px]">
                    {filters.query && (
                        <h6 className="mb-4 text-sm text-[#003545]">
                            {translate('Zoekresultaten voor')}: <strong>"{filters.query}"</strong>
                        </h6>
                    )}

                    <Table>
                        <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                            <TableRow className="[&_th]:h-auto [&_th]:py-5 [&_th]:px-2.5 [&_th]:text-left [&_th]:text-sm [&_th]:font-normal [&_th]:text-[#003545]">
                                <TableHead>{translate('Naam')}</TableHead>
                                <TableHead>{translate('E-mailadres')}</TableHead>
                                <TableHead>{translate('Rol(len)')}</TableHead>
                                <TableHead>{translate('Laatst ingelogd')}</TableHead>
                                <TableHead>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <abbr className="decoration-dotted cursor-help no-underline decoration-current underline">{translate('Beheerd')}</abbr>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {translate('Het account wordt beheerd door een extern identiteitsplatform.')}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </TableHead>
                                <TableHead>{translate('Actief')}</TableHead>
                                <TableHead>{translate('Acties')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {hasClients ? (
                                clients.data.map((client, index) => (
                                    <TableRow
                                        key={client.id}
                                        className={`[&_td]:py-5 [&_td]:px-2.5${index % 2 === 1 ? ' bg-[#f5f5f5]' : ''}`}
                                    >
                                        <TableCell>
                                            {client.name}
                                            {client.needsValidation && (
                                                <><br /><small className="text-gray-500">{translate('Wachtend op validatie')}</small></>
                                            )}
                                        </TableCell>
                                        <TableCell>{client.email}</TableCell>
                                        <TableCell>{client.roles.join(', ')}</TableCell>
                                        <TableCell>{client.lastLogin || '-'}</TableCell>
                                        <TableCell>
                                            {client.isFederated ? (
                                                <b className="text-[#0e8e6a]">{translate('Ja')}</b>
                                            ) : (
                                                translate('Nee')
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {client.isActive ? (
                                                <b className="text-[#0e8e6a]">{translate('Ja')}</b>
                                            ) : (
                                                translate('Nee')
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="inline-flex items-center gap-4">
                                                {client.canBeDeleted && (
                                                    <button
                                                        onClick={() => handleDelete(client.id, client.name)}
                                                        className="text-[#bbb] hover:text-[#dc3939] transition-all cursor-pointer"
                                                        title={translate('Verwijderen')}
                                                    >
                                                        <svg className="h-5 w-5 fill-current" role="img">
                                                            <use href="#glyph-trash-can"></use>
                                                        </svg>
                                                    </button>
                                                )}
                                                <Link
                                                    href={`/gebruikers/${client.id}/bewerken`}
                                                    className="text-[#bbb] hover:text-[#ffd400] transition-colors"
                                                    title={client.canBeEdited ? translate('Bewerken') : translate('Bekijken')}
                                                >
                                                    <svg className="h-5 w-5 fill-current" role="img">
                                                        <use href={client.canBeEdited ? '#glyph-edit' : '#glyph-view'}></use>
                                                    </svg>
                                                </Link>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow className="[&_td]:py-5 [&_td]:px-2.5 [&_td]:text-center [&_td]:text-gray-500">
                                    <TableCell colSpan={7}>
                                        {filters.query ? (
                                            <p>{translate('Er zijn geen gebruikers gevonden met de zoekopdracht ":query"', { query: filters.query })}</p>
                                        ) : (
                                            <div>
                                                <p>{translate('Er zijn nog geen gebruikers toegevoegd')}</p>
                                                {canCreateUsers && (
                                                    <Button variant="primary" asChild className="mt-4">
                                                        <Link href="/gebruikers/aanmaken">{translate('Nieuwe gebruiker uitnodigen')}</Link>
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    <Paginator links={clients?.links} />
                </div>
            </div>
        </div>
    );
}
